<script setup lang="ts">
import { OrganismReviewsProps } from "./OrganismReviews.props"
import { FEEDATY_PAGE_URL } from "~/composables/useFeedatyRating"

const props = withDefaults(defineProps<OrganismReviewsProps>(), {
  selectedStarFilter: null,
  pendingReviews: false,
  currentPage: 1,
  totalPages: 1
})
const { t } = useI18n()

const reviewsListElement = computed(() => {
  const reviews = props?.productReviews.map((review) => {
    const [first, last] = review?.reviewerName?.split(" ")
    return cleanJsonLdObject({
      "@type": "Review",
      author: {
        "@type": "Person",
        givenName:
          first || t("productPage.organismReviews.anonymousUser.givenName"),
        familyName:
          last || t("productPage.organismReviews.anonymousUser.familyName"),
        name: `${
          first || t("productPage.organismReviews.anonymousUser.givenName")
        } ${last || t("productPage.organismReviews.anonymousUser.familyName")}`
      },
      datePublished: review?.date ?? "",
      reviewBody: review?.textReview ?? "",
      reviewRating: {
        "@type": "Rating",
        bestRating: "5",
        ratingValue: review?.rating ?? 1,
        worstRating: "1"
      }
    })
  })
  return reviews
})

const emit =
  defineEmits<{
    (e: "onSearch", value: string): void
    (e: "onPageChange", page: number): void
    (e: "onStarFilter", rating: number | null): void
  }>()

useHead({
  script: [
    {
      type: "application/ld+json",
      children: JSON.stringify(
        cleanJsonLdObject({
          "@context": "https://schema.org",
          "@type": props.isProductDrug ? "Drug" : "Product",
          ...((props.reviewNumber ?? 0) > 0 &&
          (props.reviewAverage ?? "0") != "0"
            ? {
                aggregateRating: {
                  "@type": "AggregateRating",
                  ratingValue: props.reviewAverage,
                  reviewCount: props.reviewNumber
                }
              }
            : {}),
          name: props.productName,
          review: reviewsListElement.value
        })
      )
    }
  ]
})

const toggleStarFilter = (stars: number) => {
  emit(
    "onStarFilter",
    props.selectedStarFilter === stars ? null : stars
  )
}
</script>

<template>
  <div class="organism-reviews-wrapper flex flex-col gap-6 md:flex-row">
    <div>
      <div
        class="
          flex
          items-center
          justify-between
          gap-4
          md:flex-col
          md:items-start
          md:gap-0
        "
      >
        <div v-if="reviewNumber" class="flex flex-col md:mb-4">
          <h2 class="pig-bold md:elephant-bold md:mb-3">
            {{ $t("productPage.organismReviews.clientReview") }}
          </h2>
          <span class="beaver-medium md:pig-medium text-black-80">
            {{ reviewNumber }}
            {{ $t("productPage.organismReviews.totalReview") }}
          </span>
        </div>
        <div class="flex md:w-full">
          <AppLink :to="FEEDATY_PAGE_URL">
            <UtilsIcon
              name="Feedaty2.svg"
              class="h-16 w-16 md:my-2 md:h-28 md:w-28"
            />
          </AppLink>
        </div>
      </div>

      <p v-if="productName" class="beaver-bold dolphin-bold mb-6 mt-4">
        {{ productName }}
      </p>

      <div
        class="
          bg-grey-main
          flex
          min-w-max
          flex-col
          gap-3
          pb-4
          pl-3
          pr-4
          pt-3
          md:pb-10
        "
      >
        <p v-if="statsReviewAverage || reviewAverage" class="pig-medium mb-2">
          <span class="whale-bold">
            {{ statsReviewAverage || reviewAverage }}
          </span>
          {{ $t("productPage.organismReviews.onFiveStar") }}
        </p>

        <div
          v-if="reviewsByStars"
          v-for="({ reviewNumber }, index) in reviewsByStars"
          :key="`star${index}`"
          class="
            hover:bg-grey-light
            flex
            cursor-pointer
            items-center
            justify-between
            gap-2
            rounded
            px-1
            transition-colors
          "
          :class="{
            'bg-green-main/20 ring-green-main ring-1':
              selectedStarFilter === 5 - index
          }"
          @click="toggleStarFilter(5 - index)"
        >
          <UtilsStars :size="5" :stars="5 - index" />
          <p class="beaver-medium text-black-80 whitespace-nowrap underline">
            {{ reviewNumber }}
            {{ $t("productPage.organismReviews.reviews") }}
          </p>
        </div>
      </div>
    </div>

    <div class="flex w-full flex-col gap-6">
      <div
        class="
          flex flex-col
          gap-6
          md:max-w-[65%]
          md:flex-row
          md:items-center
          md:justify-between
          md:gap-7
        "
      >
        <SearchSearchbar
          v-if="searchSettings"
          v-bind="searchSettings"
          @update:modelValue="(newValue) => emit('onSearch', newValue)"
          :placeholder="$t('productPage.organismReviews.findKeyword')"
        />

        <!-- ! In the future advanced filtering may be supported -->
        <!--
        <div class="flex flex-shrink-0 items-center gap-4">
          <span class="beaver-medium ml-2">
            {{ $t("productPage.organismReviews.orderBy") }}
          </span>

          <TextfieldsDropdown
            v-if="dropdownMenu"
            :defaultSelected="dropdownMenu[0].label"
            :dropdownMenu="dropdownMenu"
          />
        </div>
        -->
      </div>

      <div
        class="flex flex-col gap-6 transition-opacity"
        :class="{ 'opacity-50': pendingReviews }"
      >
        <ProductPageReviewsMoleculeReview
          v-if="productReviews.length"
          v-for="review in productReviews"
          :key="`${review.reviewerName}-${review.date}-${review.rating}`"
          v-bind="review"
        />
      </div>

      <NavigationStaticPaginator
        v-if="totalPages > 1"
        class="mx-auto"
        :page="currentPage"
        :total-pages="totalPages"
        hide-labels
        @page-change="emit('onPageChange', $event)"
      />
    </div>
  </div>
</template>
